import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import ReadingProgress from './ui/ReadingProgress';
import { announceReadingStarted } from './ui/ReadingTray';
import './DeliveryQualityCertificateModal.css';

/**
 * "Fill from Certificate" for the delivery product quality table.
 *
 * Reads a Certificate of Analysis and PROPOSES values. Nothing is written by
 * the API -- this modal hands the accepted values back to DeliveryForm, which
 * merges them into formData, and the normal delivery save persists them. That
 * keeps DeliveryViewSet.update the single writer of the quality fields (it
 * owns the custom-row __metadata__ id map). See
 * backend/deliveries/services/coa_extraction_service.py for the full reason.
 *
 * The read itself runs in the background: a scanned certificate takes minutes,
 * far longer than a web request may be held open. "Read certificate" queues it
 * and this modal checks back every few seconds until it is done or failed.
 */

// How often to check on a queued certificate reading.
export const READING_POLL_MS = 5000;

const COLUMNS = [
    {
        key: 'loading_port',
        label: 'Loading Port Actual Quality',
        hint: 'Certificate issued at the load port',
    },
    {
        key: 'discharging_port',
        label: 'Discharging Port Actual Quality',
        hint: 'Certificate issued at the discharge port / outturn',
    },
];

const DeliveryQualityCertificateModal = ({ deliveryId, onClose, onApply }) => {
    const [column, setColumn] = useState('loading_port');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    // The background reading while it is queued or being read: { id, state }.
    const [reading, setReading] = useState(null);

    // Documents already filed against this delivery. The COA is usually
    // already here -- uploading it again stores the same paper twice.
    const [documents, setDocuments] = useState([]);
    const [documentsError, setDocumentsError] = useState(null);
    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [selectedDocumentId, setSelectedDocumentId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setDocumentsLoading(true);
        axiosInstance
            .get(`/api/deliveries/${deliveryId}/quality-certificates/`)
            .then((response) => {
                if (cancelled) return;
                setDocuments(response.data?.documents || []);
                setDocumentsError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                // Say the list failed. Showing an empty list would read as
                // "nothing on file" and send the operator off to re-upload --
                // the duplication this picker exists to remove.
                setDocuments([]);
                setDocumentsError(
                    err.response?.data?.error ||
                    "Could not load this delivery's documents."
                );
            })
            .finally(() => { if (!cancelled) setDocumentsLoading(false); });
        return () => { cancelled = true; };
    }, [deliveryId]);

    const chooseDocument = (id) => {
        setSelectedDocumentId(id);
        setFile(null);          // the two inputs are alternatives, never both
        setError(null);
    };

    const chooseFile = (chosen) => {
        setFile(chosen);
        setSelectedDocumentId(null);
        setError(null);
    };

    const hasSource = !!file || !!selectedDocumentId;

    // Row key -> accepted?  Standard rows keyed by field name, custom rows by
    // "custom:<property name>". Everything starts accepted; the operator
    // unticks what they disagree with.
    const [accepted, setAccepted] = useState({});
    // Standard field -> chosen value, so an operator can pick the ADB figure
    // when the certificate reported the same parameter on several bases.
    const [chosenValue, setChosenValue] = useState({});

    const showProposals = (data) => {
        setResult(data);

        const initialAccepted = {};
        const initialValues = {};
        (data.standard || []).forEach((row) => {
            initialAccepted[row.field] = true;
            initialValues[row.field] = row.value;
        });
        (data.custom || []).forEach((row) => {
            initialAccepted[`custom:${row.property_name}`] = true;
        });
        setAccepted(initialAccepted);
        setChosenValue(initialValues);
    };

    // One place that acts on a reading, whether it came back from starting it
    // or from checking on it: keep waiting, show the proposals, or say why not.
    const handleReading = (data) => {
        if (data.state === 'done') {
            setReading(null);
            setLoading(false);
            showProposals(data.result);
        } else if (data.state === 'failed') {
            setReading(null);
            setLoading(false);
            setError(data.error || 'Could not read this certificate.');
        } else {
            if (!reading) announceReadingStarted();
            setReading({ id: data.id, state: data.state, progress: data.progress || null });
        }
    };

    const readingInFlight = !!reading;

    // Reopening the window picks up a reading this person started on this
    // delivery that is still going - or the one the app-wide tray's link names -
    // instead of offering to read the certificate all over again.
    useEffect(() => {
        let cancelled = false;
        const resume = async () => {
            try {
                let readingId = new URLSearchParams(window.location.search).get('certificate_reading');
                if (!readingId) {
                    const mine = await axiosInstance.get('/api/reading-progress/mine/');
                    const match = (mine.data?.items || []).find((item) => (
                        item.kind === 'certificate_reading' && String(item.delivery_id) === String(deliveryId)
                    ));
                    readingId = match?.id;
                }
                if (!readingId || cancelled) return;
                const response = await axiosInstance.get(
                    `/api/deliveries/${deliveryId}/certificate-readings/${readingId}/`
                );
                if (cancelled) return;
                if (response.data?.column) setColumn(response.data.column);
                if (response.data?.state !== 'done' && response.data?.state !== 'failed') setLoading(true);
                handleReading(response.data);
            } catch (error) {
                // Nothing to pick up is the normal case.
            }
        };
        resume();
        return () => { cancelled = true; };
        // handleReading only calls state setters, so it need not be a dependency.
    }, [deliveryId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!readingInFlight) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const response = await axiosInstance.get(
                    `/api/deliveries/${deliveryId}/certificate-readings/${reading.id}/`
                );
                if (!cancelled) handleReading(response.data);
            } catch (err) {
                if (cancelled) return;
                if (err.response?.status === 404 || err.response?.status === 403) {
                    // The reading is gone or no longer ours to see; waiting longer
                    // would only keep the spinner going.
                    setReading(null);
                    setLoading(false);
                    setError(err.response?.data?.error || 'Could not check on this certificate.');
                } else {
                    // A missed check (network blip) is simply tried again.
                    setReading((current) => (current ? { ...current } : current));
                }
            }
        }, READING_POLL_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // handleReading only calls state setters, so it need not be a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readingInFlight, reading, deliveryId]);

    const handleAnalyse = async () => {
        if (!hasSource) {
            setError('Choose a document on this delivery, or upload a file.');
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);

        const payload = new FormData();
        if (file) {
            payload.append('file', file);
        } else {
            payload.append('document_id', selectedDocumentId);
        }
        payload.append('column', column);

        try {
            const response = await axiosInstance.post(
                `/api/deliveries/${deliveryId}/extract-quality-from-certificate/`,
                payload,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            handleReading(response.data);
        } catch (err) {
            // Refusals the request can make straight away (wrong document, file
            // too large, reading service down) carry their own sentence.
            setError(err.response?.data?.error || 'Could not read this certificate.');
            setLoading(false);
        }
    };

    const toggle = (key) => setAccepted((prev) => ({ ...prev, [key]: !prev[key] }));

    const acceptedCount =
        Object.values(accepted).filter(Boolean).length;

    const handleApply = () => {
        const standard = (result.standard || [])
            .filter((row) => accepted[row.field])
            .map((row) => ({ field: row.field, value: chosenValue[row.field] ?? row.value }));

        const custom = (result.custom || [])
            .filter((row) => accepted[`custom:${row.property_name}`])
            .map((row) => ({
                propertyName: row.property_name,
                customKey: row.custom_key,
                value: row.value,
            }));

        onApply({ standard, custom, column: result.column, certificate: result.certificate });
    };

    const columnLabel = COLUMNS.find((c) => c.key === column)?.label;

    // The certificate may state where it was sampled. If that disagrees with
    // the column the operator picked, say so -- it is the single most likely
    // way to put good numbers in the wrong column.
    const stage = result?.certificate?.sampling_stage;
    const stageMismatch =
        stage &&
        ((stage === 'LOADING' && result.column === 'discharging_port') ||
            (stage === 'DISCHARGING' && result.column === 'loading_port'));

    return (
        <div className="dqc-overlay" onClick={onClose}>
            <div className="dqc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="dqc-header">
                    <h3>Fill Quality from Certificate</h3>
                    <button type="button" className="dqc-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {!result && (
                    <div className="dqc-body">
                        <p className="dqc-intro">
                            Upload a Certificate of Analysis / Sampling. The values are read from
                            the certificate and shown for your confirmation — nothing is saved
                            until you apply them and save the delivery.
                        </p>

                        <label className="dqc-label">Fill which column?</label>
                        <div className="dqc-columns">
                            {COLUMNS.map((option) => (
                                <button
                                    type="button"
                                    key={option.key}
                                    className={`dqc-column-option ${column === option.key ? 'is-selected' : ''}`}
                                    onClick={() => setColumn(option.key)}
                                >
                                    <span className="dqc-column-title">{option.label}</span>
                                    <span className="dqc-column-hint">{option.hint}</span>
                                </button>
                            ))}
                        </div>
                        <p className="dqc-note">
                            The Sales Contract and IMT Reference columns are the agreed spec, so
                            they cannot be filled from a laboratory certificate.
                        </p>

                        <label className="dqc-label">Documents on this delivery</label>
                        {documentsLoading && (
                            <div className="dqc-doclist-empty">Loading documents…</div>
                        )}
                        {!documentsLoading && documentsError && (
                            <div className="dqc-error">{documentsError}</div>
                        )}
                        {!documentsLoading && !documentsError && documents.length === 0 && (
                            <div className="dqc-doclist-empty">
                                No documents are filed against this delivery yet — upload the
                                certificate below.
                            </div>
                        )}
                        {!documentsLoading && documents.length > 0 && (
                            <div className="dqc-doclist">
                                {documents.map((doc) => (
                                    <button
                                        type="button"
                                        key={doc.id}
                                        className={`dqc-doc ${selectedDocumentId === doc.id ? 'is-selected' : ''}`}
                                        onClick={() => chooseDocument(doc.id)}
                                    >
                                        <span className="dqc-doc-name">{doc.name}</span>
                                        <span className="dqc-doc-meta">
                                            {doc.is_certificate && (
                                                <span className="dqc-doc-badge">Certificate</span>
                                            )}
                                            {doc.document_type}
                                            {doc.date_uploaded ? ` · ${doc.date_uploaded}` : ''}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        <label className="dqc-label" htmlFor="dqc-file">
                            {documents.length > 0 ? 'Or upload a new file' : 'Certificate file'}
                        </label>
                        <input
                            id="dqc-file"
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.docx,.xlsx"
                            onChange={(e) => chooseFile(e.target.files[0] || null)}
                        />

                        {error && <div className="dqc-error">{error}</div>}
                        {readingInFlight && (
                            <div className="dqc-doclist-empty">
                                <div style={{ marginBottom: '0.5rem' }}>
                                    Reading the certificate. A scanned certificate can take a few
                                    minutes — keep this window open and the values will appear here.
                                </div>
                                <ReadingProgress compact progress={reading.progress} />
                            </div>
                        )}

                        <div className="dqc-actions">
                            <button type="button" className="dqc-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="dqc-primary"
                                onClick={handleAnalyse}
                                disabled={loading || !hasSource}
                            >
                                {loading ? 'Reading certificate…' : 'Read certificate'}
                            </button>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="dqc-body">
                        <div className="dqc-cert-summary">
                            <strong>{result.certificate?.inspection_company || 'Certificate'}</strong>
                            {result.certificate?.number ? ` · ${result.certificate.number}` : ''}
                            {result.certificate?.date ? ` · ${result.certificate.date}` : ''}
                            <div className="dqc-cert-target">Filling: {columnLabel}</div>
                        </div>

                        {stageMismatch && (
                            <div className="dqc-warning">
                                This certificate says it was sampled at{' '}
                                <strong>{stage === 'LOADING' ? 'loading' : 'discharging'}</strong>, but
                                you chose the {columnLabel} column. Check before applying.
                            </div>
                        )}

                        {(result.standard || []).length > 0 && (
                            <table className="dqc-table">
                                <thead>
                                    <tr>
                                        <th className="dqc-tick" />
                                        <th>Property</th>
                                        <th>On certificate</th>
                                        <th>Current</th>
                                        <th>New value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.standard.map((row) => (
                                        <tr key={row.field} className={accepted[row.field] ? '' : 'is-rejected'}>
                                            <td className="dqc-tick">
                                                <input
                                                    type="checkbox"
                                                    checked={!!accepted[row.field]}
                                                    onChange={() => toggle(row.field)}
                                                />
                                            </td>
                                            <td>{row.label}</td>
                                            <td className="dqc-source">
                                                {row.certificate_label}
                                                {row.unit ? ` (${row.unit})` : ''}
                                            </td>
                                            <td className="dqc-current">{row.current_value ?? '—'}</td>
                                            <td>
                                                {row.alternatives && row.alternatives.length > 0 ? (
                                                    <select
                                                        value={chosenValue[row.field]}
                                                        onChange={(e) =>
                                                            setChosenValue((prev) => ({
                                                                ...prev,
                                                                [row.field]: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value={row.value}>
                                                            {row.value}
                                                            {row.basis ? ` (${row.basis})` : ''}
                                                        </option>
                                                        {row.alternatives.map((alt, i) => (
                                                            <option key={i} value={alt.value}>
                                                                {alt.value}
                                                                {alt.basis ? ` (${alt.basis})` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="dqc-new">{row.value}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {(result.custom || []).length > 0 && (
                            <>
                                <div className="dqc-subhead">
                                    Extra parameters on this certificate — these will be added as
                                    custom rows
                                </div>
                                <table className="dqc-table">
                                    <tbody>
                                        {result.custom.map((row) => (
                                            <tr
                                                key={row.property_name}
                                                className={accepted[`custom:${row.property_name}`] ? '' : 'is-rejected'}
                                            >
                                                <td className="dqc-tick">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!accepted[`custom:${row.property_name}`]}
                                                        onChange={() => toggle(`custom:${row.property_name}`)}
                                                    />
                                                </td>
                                                <td>{row.property_name}</td>
                                                <td className="dqc-source">{row.unit || ''}</td>
                                                <td className="dqc-current">—</td>
                                                <td><span className="dqc-new">{row.value}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {(result.skipped || []).length > 0 && (
                            <div className="dqc-skipped">
                                Not proposed (no result on the certificate):{' '}
                                {result.skipped.map((s) => s.certificate_label).join(', ')}
                            </div>
                        )}

                        {error && <div className="dqc-error">{error}</div>}

                        <div className="dqc-actions">
                            <button
                                type="button"
                                className="dqc-secondary"
                                onClick={() => { setResult(null); setError(null); }}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="dqc-primary"
                                onClick={handleApply}
                                disabled={acceptedCount === 0}
                            >
                                Apply {acceptedCount} value{acceptedCount === 1 ? '' : 's'}
                            </button>
                        </div>
                        <p className="dqc-note">
                            Applying fills the form only. Save the delivery to keep the changes.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeliveryQualityCertificateModal;
